import Dexie, { type Table } from 'dexie';
import { DATA_FORMAT_VERSION, type AnyEnvelope } from '@wiztime/core';
import type { OpLogEntry } from '../oplog';
import { MetaKey } from '../repo';

/** A stored record: the envelope plus `pd`, a top-level copy of the personal date ('' when none). */
export type StoredRecord = AnyEnvelope & { pd: string };

export interface MetaEntry {
  key: string;
  value: string;
}

export type WizDb = Dexie & {
  records: Table<StoredRecord, string>;
  meta: Table<MetaEntry, string>;
  oplog: Table<OpLogEntry, number>;
};

/** The backup store of the data-format migration (data dictionary §10). */
export type WizBackupDb = Dexie & { records: Table<StoredRecord, string>; meta: Table<MetaEntry, string> };

export function backupDbName(name: string): string {
  return `${name}-backup`;
}

export function createBackupDb(name: string): WizBackupDb {
  const db = new Dexie(backupDbName(name)) as WizBackupDb;
  db.version(1).stores({ records: 'id', meta: 'key' });
  return db;
}

export const DEFAULT_DB_NAME = 'wiztime';

const DB_INSTANCE_ID_BYTES = 16;

/**
 * 32 random hex digits. `getRandomValues` rather than `randomUUID`: the latter exists only in
 * secure contexts, and the app must also open from a plain `http:` address on a local network.
 */
export function newDbInstanceId(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(DB_INSTANCE_ID_BYTES));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Open the WizTime database. `records` is keyed by `id` with indexes on `type`, `[type+pd]`
 * (personal-date lookups) and `hlc` (outbox); `meta` holds device-local key/value entries;
 * `oplog` (version 2) is the device-local operation log. Table versions describe tables and
 * indexes only; the meaning of the data is versioned by `MetaKey.DataFormatVersion`.
 */
export function createWizDb(name: string = DEFAULT_DB_NAME): WizDb {
  const db = new Dexie(name) as WizDb;
  db.version(1).stores({
    records: 'id, type, [type+pd], hlc',
    meta: 'key',
  });
  db.version(2).stores({
    records: 'id, type, [type+pd], hlc',
    meta: 'key',
    oplog: '++seq, at, kind',
  });
  db.on('populate', (tx) => {
    const meta = tx.table<MetaEntry, string>('meta');
    void meta.add({ key: MetaKey.DbInstanceId, value: newDbInstanceId() });
    // A new database holds no data of an older format.
    void meta.add({ key: MetaKey.DataFormatVersion, value: String(DATA_FORMAT_VERSION) });
  });
  return db;
}
